import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getNetworks } from '@/services/api/networks';
import {
    formatNetwork,
    getSelectedNetwork,
    limparFiltrosDeDominio,
    resolverSelecao,
    setSelectedNetwork,
    type GamNetwork,
} from '@/lib/gamNetwork';

interface NetworkCtx {
    networks: GamNetwork[];
    selected: string | null;
    selectedNetwork: GamNetwork | undefined;
    setSelected: (networkCode: string) => void;
    format: (networkCode: string | null | undefined) => string;
    isLoading: boolean;
}

const Ctx = createContext<NetworkCtx | null>(null);

export function NetworkProvider({ children }: { children: React.ReactNode }) {
    const queryClient = useQueryClient();
    const [selected, setSelectedState] = useState<string | null>(getSelectedNetwork());

    const { data: networks = [], isLoading } = useQuery({
        queryKey: ['gam-networks'],
        queryFn: getNetworks,
        staleTime: 1000 * 60 * 60,
    });

    // Primeira carga: valida a conta salva contra a lista (ou cai na padrão).
    // Se a conta mudou (primeiro acesso, ou a salva saiu da lista), o que as
    // telas já buscaram foi sem conta ou com a errada: busca de novo.
    useEffect(() => {
        if (!networks.length) return;
        const conta = resolverSelecao(networks, getSelectedNetwork());
        if (conta !== getSelectedNetwork()) {
            setSelectedNetwork(conta);
            queryClient.resetQueries({ predicate: (q) => q.queryKey[0] !== 'gam-networks' });
        }
        setSelectedState(conta);
    }, [networks, queryClient]);

    const setSelected = useCallback(
        (networkCode: string) => {
            if (networkCode === getSelectedNetwork()) return;
            setSelectedNetwork(networkCode);
            limparFiltrosDeDominio();
            setSelectedState(networkCode);
            // Tudo o que está em cache foi buscado com a conta anterior. Resetar
            // (e não só invalidar) tira os dados velhos da tela na hora, em vez
            // de mostrar a outra conta até o refetch terminar.
            queryClient.resetQueries({ predicate: (q) => q.queryKey[0] !== 'gam-networks' });
        },
        [queryClient],
    );

    const value = useMemo<NetworkCtx>(
        () => ({
            networks,
            selected,
            selectedNetwork: networks.find((n) => n.network_code === selected),
            setSelected,
            format: (code) => formatNetwork(code, networks),
            isLoading,
        }),
        [networks, selected, setSelected, isLoading],
    );

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// Roda `aoTrocar` quando a conta muda depois que a tela montou (não na
// primeira renderização). Para estado local de tela que guarda domínios da
// conta anterior, como os domínios selecionados no Dashboard.
export function useAoTrocarConta(aoTrocar: () => void): void {
    const { selected } = useNetwork();
    const anterior = useRef(selected);
    const callback = useRef(aoTrocar);
    callback.current = aoTrocar;
    useEffect(() => {
        if (anterior.current !== null && anterior.current !== selected) callback.current();
        anterior.current = selected;
    }, [selected]);
}

export function useNetwork(): NetworkCtx {
    const ctx = useContext(Ctx);
    if (!ctx) throw new Error('useNetwork precisa estar dentro de <NetworkProvider>');
    return ctx;
}
